import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Select, DatePicker, Input, InputNumber, Button, Table, Tag, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import moment from 'moment';
import { Box, Flex, Text } from 'atoms';
import { createClientPOV2, createClientWOV2 } from 'store/clientInvoiceSliceV2';
import InvoiceSlabTable from 'componentsv2/common/InvoiceSlabTable';
import AssignSacCodeModal from './AssignSacCodeModal';
import { buildTaxSummary, buildTotals, computeLineTax, formatRate, q2 } from '../taxSummary';

const { Option } = Select;
const { TextArea } = Input;

// Standard Indian bank formats
const ACCOUNT_NUMBER_RE = /^[0-9]{9,18}$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const EXEMPT_SAC = '__EXEMPT__';
const EXEMPT_SAC_CODE = '996729-Exempt';
const ADDITIONAL_CHARGE_LABEL = 'Additional Charges';
const sacTaxRate = (sac, sacOptions = []) => sacOptions.find((o) => o.value === sac)?.rate ?? 0;
const partyIdRows = (gstin, pan) =>
	[gstin && { label: 'GST:', value: gstin }, pan && { label: 'PAN Number:', value: pan }].filter(
		Boolean
	);

const DEFAULT_BANK = {
	bank_name: 'HDFC Bank',
	account_number: '50200037571741',
	ifsc: 'HDFC0000832',
	account_type: 'Current',
};

const ACCOUNT_TYPE_OPTIONS = [
	{ value: 'Current', label: 'Current' },
	{ value: 'Saving', label: 'Saving' },
];
const ADDITIONAL_CHARGE_TYPES = [
	'Halt Charges',
	'Miscellaneous Charges',
	'Toll Charges',
	'Unloading Charges',
	'Extra KM',
	'Extra HR',
	'Labour Charges',
	'Deduction Amount',
];
const normaliseIssuer = (i = {}) => ({
	value: i.value ?? i.id,
	label: i.label || i.address || i.legal_name || '-',
	gstin: i.gstin,
	pan: i.pan,
	legalName: i.legal_name || i.name,
	address: i.address,
	website: i.website,
	signatory: i.signatory,
	contactEmail: i.contact_email,
	banks: i.banks || [],
	terms: i.terms || [],
	bankDetails: i.bank_details || {},
});

const Sheet = styled(Box)`
	background: #fff;
	border-radius: 8px;
	padding: 32px 40px;
	box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const InvoiceTitle = styled.div`
	font-size: 26px;
	font-weight: 800;
	color: #1e293b;
	letter-spacing: 1px;
`;

const Logo = styled.img`
	height: 60px;
	width: auto;
	object-fit: contain;
`;

const SellerName = styled.div`
	color: #1e3a5f;
	font-weight: 700;
	font-size: 16px;
	margin: 16px 0 8px;
`;

const SectionLabel = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: #475569;
	margin-bottom: 6px;
`;

const Muted = styled.div`
	color: #64748b;
	font-size: 12px;
	line-height: 1.5;
	/* Wrap long addresses (seller / Bill To / Ship To) onto multiple lines. */
	white-space: normal;
	overflow-wrap: anywhere;
	word-break: break-word;
`;

const ErrorText = styled.div`
	color: #ef4444;
	font-size: 12px;
	margin-top: 4px;
`;

const Divider = styled.div`
	border-top: 1px solid #e5e7eb;
	margin: 20px 0;
`;

const ActionLink = styled(Button)`
	color: #1e3a5f;
	border-color: #cbd5e1;
	border-radius: 6px;
`;
const ChargeSelect = styled(Select)`
	.ant-select-selector {
		border-color: #cbd5e1 !important;
		border-radius: 6px !important;
	}
	.ant-select-selection-placeholder {
		color: #1e3a5f;
		font-weight: 500;
	}
`;

const PrimaryButton = styled(Button)`
	height: 40px;
	padding: 0 28px;
	background-color: #1e3a5f;
	border-color: #1e3a5f;
	color: #fff;
	border-radius: 6px;
	font-weight: 500;

	&:hover,
	&:focus {
		background-color: #18405d !important;
		border-color: #18405d !important;
		color: #fff !important;
	}
`;

const TotalsBox = styled.div`
	width: 280px;
	margin-left: auto;
	margin-top: 16px;
`;

const TotalsRow = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 6px 12px;
	font-size: 13px;
	color: #475569;
`;

const GrandTotalRow = styled(TotalsRow)`
	background: #f3f4f6;
	color: #15233a;
	font-weight: 700;
	font-size: 15px;
	border-radius: 4px;
`;

const formatINR = (value) => {
	const num = Number(value) || 0;
	return `₹ ${num.toLocaleString('en-IN')}`;
};

let rowSeq = 1;
const nextRowId = () => {
	rowSeq += 1;
	return rowSeq;
};
const makeRow = (overrides = {}) => ({
	key: `row-${nextRowId()}`,
	item: '',
	description: '',
	sac_code: undefined,
	tax_rate: 0,
	qty: 1,
	rate: 0,
	is_additional: false,
	...overrides,
});

const computeRow = (row, taxMode) => {
	const qty = Number(row.qty) || 0;
	const rate = Number(row.rate) || 0;
	// A deduction is a discount on the supply, so it is carried as a negative amount
	// taxed at its own slab. That single sign then nets it out of the subtotal, the
	// slab's tax basis and the heads together (taxSummary.js), rather than each of
	// the three applying its own rule. The user still types a positive figure.
	const amount = (row.is_deduction ? -1 : 1) * qty * rate;
	const { cgst, sgst, igst, taxAmount } = computeLineTax(amount, row.tax_rate, taxMode);
	return {
		...row,
		amount,
		cgst,
		sgst,
		igst,
		taxAmount,
		grossAmount: +(amount + taxAmount).toFixed(2),
	};
};

/**
 * Why the deductions on this invoice cannot be applied to the tax basis — or null.
 *
 * Both cases are refused again by _apply_gst_to_items server-side; caught here so
 * the user is stopped before the review modal rather than after it.
 */
const deductionError = (computedRows) => {
	const deduction = computedRows.reduce(
		(sum, r) => (r.is_deduction ? sum - (Number(r.amount) || 0) : sum),
		0
	);
	if (deduction <= 0) return null;
	const slabs = new Map();
	computedRows.forEach((r) => {
		const rate = Number(r.tax_rate) || 0;
		if (rate > 0) slabs.set(rate, (slabs.get(rate) || 0) + (Number(r.amount) || 0));
	});
	// A deduction comes off the taxable value, so with two taxable rates on one
	// invoice there is no single basis for it to reduce. Enter one deduction per rate.
	if (slabs.size > 1) {
		return 'This invoice is billed at more than one tax rate — enter a separate deduction for each.';
	}
	const taxable = [...slabs.values()].reduce((sum, v) => sum + v, 0);
	if (slabs.size && q2(taxable - deduction) < 0) {
		return 'Deductions exceed the taxable value of this invoice.';
	}
	// A fully exempt invoice (warehousing) has no tax to relieve, so its deductions
	// only reduce what is billed — but not below zero.
	const total = computedRows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
	if (q2(total) < 0) {
		return 'Deductions exceed the value of this invoice.';
	}
	return null;
};

const seedAdditionalCharges = (context) =>
	(context?.additionalCharges || []).map((c) => {
		// Stored discounts come back as negative amounts; re-hydrate them as deduction
		// rows holding the positive figure, so a regenerated invoice edits the way the
		// original was entered instead of showing a negative 'charge'.
		const amount = Number(c.amount ?? c.rate) || 0;
		const isDeduction = c.is_deduction ?? amount < 0;
		return makeRow({
			item: c.item_code || undefined,
			description: c.description || ADDITIONAL_CHARGE_LABEL,
			sac_code: c.sac || undefined,
			tax_rate: sacTaxRate(c.sac, context?.sacCodes),
			qty: 1,
			rate: Math.abs(amount),
			is_additional: true,
			is_deduction: isDeduction,
		});
	});

const buildPreviewRows = (previewLines, defaultSac, exempt) =>
	previewLines.map((li) =>
		makeRow({
			item: li.item_code,
			description: li.description,
			// Non-GST / warehouse → force the exempt SAC (0%), overriding any rated SAC
			// the preview built. Otherwise keep the preview's SAC + tax rate.
			sac_code: exempt ? defaultSac : li.sac,
			tax_rate: exempt ? 0 : Number(li.tax_rate) || 0,
			qty: Number(li.qty) || 1,
			rate: Number(li.rate) || 0,
			trip_ids: li.trip_ids || [],
		})
	);

const buildTripRows = (trips, defaultSac) =>
	trips.map((t) =>
		makeRow({
			description: `Transport - ${t.trip_code || t.trip_id || ''}`.trim(),
			qty: 1,
			rate: Number(t.base_fare) || 0,
			trip_ids: [t.trip_id],
			sac_code: defaultSac,
		})
	);

const seedRows = (context) => {
	const additional = seedAdditionalCharges(context);
	const exemptDefault = Boolean(context?.isWarehouseFlow);
	const defaultSac = exemptDefault ? EXEMPT_SAC_CODE : undefined;
	const previewLines = context?.preview?.line_items;
	if (previewLines?.length) {
		return [...buildPreviewRows(previewLines, defaultSac, exemptDefault), ...additional];
	}
	if (context?.isWarehouseFlow) {
		return [
			makeRow({ description: 'Warehousing Charges', qty: 1, rate: 0, sac_code: defaultSac }),
			...additional,
		];
	}
	const trips = context?.selectedTrips || [];
	if (!trips.length) return [makeRow({ sac_code: defaultSac }), ...additional];
	return [...buildTripRows(trips, defaultSac), ...additional];
};

// eslint-disable-next-line sonarjs/cognitive-complexity
function InvoiceGenerationStep({ context, onBack, onGenerate, generateLabel }) {
	// Regeneration prefill (edit flow) — falls back to fresh Create-Invoice defaults
	// when absent. Mount this component with a `key` tied to the prefill data's
	// readiness so these useState initializers pick up the loaded values.
	const prefill = context?.prefill || {};
	const [rows, setRows] = useState(() => seedRows(context));
	const [selectedKeys, setSelectedKeys] = useState([]);
	const [sacModalOpen, setSacModalOpen] = useState(false);
	const [billTo, setBillTo] = useState(prefill.bill_to_address || context?.billTo || '');
	const [shipTo, setShipTo] = useState(prefill.ship_to_address || context?.shipTo || '');

	// All dropdown masters come from context (to be wired to a backend source — TBD with backend).
	const issuers = (context?.issuers || []).map(normaliseIssuer);
	const poOptions = context?.poOptions || [];
	const woOptions = context?.woOptions || [];
	const accountTypes = context?.accountTypes?.length
		? context.accountTypes
		: ACCOUNT_TYPE_OPTIONS;
	const sacOptions = context?.sacCodes || [];

	const initialDueDate = prefill.due_date || context?.preview?.suggested_due_date;
	const initialIssuer = issuers.find((o) => o.value === prefill.bill_from) || issuers[0];
	// All client-invoice flows pre-fill Ripplr's default bank details (editable by the user).
	const defaultBank = DEFAULT_BANK;
	const [meta, setMeta] = useState({
		bill_from: initialIssuer?.value,
		due_date: initialDueDate ? moment(initialDueDate) : null,
		po_number: prefill.po_number ?? poOptions[0]?.value,
		wo_number: prefill.wo_number ?? undefined,
		bank: prefill.bank ?? defaultBank.bank_name,
		account_number: prefill.account_number ?? defaultBank.account_number,
		ifsc: prefill.ifsc ?? defaultBank.ifsc,
		account_type: prefill.account_type ?? defaultBank.account_type,
		remarks: prefill.remarks || '',
	});

	// Issuer name/GST/PAN/banks/terms/bank details shown on the invoice come from
	// the selected issuer (Ripplr Bill-From entity).
	const selectedIssuer = issuers.find((o) => o.value === meta.bill_from);
	const bankOptions = selectedIssuer?.banks?.length ? selectedIssuer.banks : context?.banks || [];
	// Dedupe bank names (the raw list has one row per account, so banks repeat); always
	// include the selected issuer's own bank so the pre-filled value has a matching option.
	const issuerBankName = selectedIssuer?.bankDetails?.bank_name;
	const defaultBankName = DEFAULT_BANK.bank_name;
	const uniqueBankOptions = Array.from(
		new Map(
			[
				...(issuerBankName ? [{ value: issuerBankName, label: issuerBankName }] : []),
				...(defaultBankName ? [{ value: defaultBankName, label: defaultBankName }] : []),
				...bankOptions,
			].map((o) => [o.value, o])
		).values()
	);
	const terms = selectedIssuer?.terms || [];
	const [clientGstin, setClientGstin] = useState(context?.clientGstin || '');
	const clientPan =
		context?.clientPan || (clientGstin.length >= 12 ? clientGstin.slice(2, 12) : '');
	const sameState =
		selectedIssuer?.gstin &&
		clientGstin &&
		selectedIssuer.gstin.slice(0, 2) === clientGstin.slice(0, 2);
	const isNonGstClient = !clientGstin.trim();
	const [taxModeChoice, setTaxModeChoice] = useState(
		prefill.tax_mode || ((context?.clientGstin || '').trim() ? 'IGST' : 'NIL')
	);
	let taxMode;
	if (isNonGstClient) {
		taxMode = taxModeChoice;
	} else {
		taxMode = sameState ? 'CGST_SGST' : 'IGST';
	}

	const displayRows = useMemo(
		() =>
			taxMode === 'NIL'
				? rows.map((r) => ({ ...r, sac_code: EXEMPT_SAC_CODE, tax_rate: 0 }))
				: rows,
		[rows, taxMode]
	);
	const computedRows = useMemo(
		() => displayRows.map((r) => computeRow(r, taxMode)),
		[displayRows, taxMode]
	);

	const totals = useMemo(() => buildTotals(computedRows, taxMode), [computedRows, taxMode]);

	// Present only for a warehousing project; the table renders itself from this.
	const warehouseCtx = context?.isWarehouseFlow ? context?.preview?.warehouse_context : null;

	const updateRow = (key, patch) =>
		setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

	const removeRow = (key) => setRows((prev) => prev.filter((r) => r.key !== key));

	const setMetaField = (key, value) => setMeta((prev) => ({ ...prev, [key]: value }));

	// Bank-field format validation
	const accountError =
		meta.account_number && !ACCOUNT_NUMBER_RE.test(meta.account_number)
			? 'Account number must be 9–18 digits'
			: '';
	const ifscError =
		meta.ifsc && !IFSC_RE.test(meta.ifsc) ? 'Invalid IFSC (e.g. HDFC0001234)' : '';
	const dispatch = useDispatch();
	const [newPo, setNewPo] = useState('');
	// The invoice can bill several projects; a PO/WO belongs to exactly one, so a
	// newly added order is scoped to the primary (first-picked) project — the same
	// one the invoice header stores.
	const primaryProjectId = context?.filters?.project_ids?.[0] ?? context?.filters?.project_id;

	const [addingPo, setAddingPo] = useState(false);
	const [localPoOptions, setLocalPoOptions] = useState([]);
	const allPoOptions = useMemo(() => {
		const seen = new Set();
		return [...poOptions, ...localPoOptions].filter((o) => {
			if (seen.has(o.value)) return false;
			seen.add(o.value);
			return true;
		});
	}, [poOptions, localPoOptions]);

	const handleAddPo = async () => {
		const value = newPo.trim();
		if (!value) {
			message.error('Enter a PO number');
			return;
		}
		if (allPoOptions.some((o) => o.value === value)) {
			setMetaField('po_number', value);
			setNewPo('');
			message.info('PO already exists');
			return;
		}
		setAddingPo(true);
		try {
			const po = await dispatch(
				createClientPOV2({
					po_number: value,
					client_id: context?.filters?.client_id,
					project_id: primaryProjectId,
				})
			);
			const label = po?.valid_till ? `${value} (valid till ${po.valid_till})` : value;
			setLocalPoOptions((prev) => [...prev, { value, label }]);
			setMetaField('po_number', value);
			setNewPo('');
			message.success('PO added');
		} catch (err) {
			message.error('Failed to add PO');
		} finally {
			setAddingPo(false);
		}
	};

	// WO Number — mirror of the PO Number workflow
	const [newWo, setNewWo] = useState('');
	const [addingWo, setAddingWo] = useState(false);
	const [localWoOptions, setLocalWoOptions] = useState([]);
	const allWoOptions = useMemo(() => {
		const seen = new Set();
		return [...woOptions, ...localWoOptions].filter((o) => {
			if (seen.has(o.value)) return false;
			seen.add(o.value);
			return true;
		});
	}, [woOptions, localWoOptions]);

	const handleAddWo = async () => {
		const value = newWo.trim();
		if (!value) {
			message.error('Enter a WO number');
			return;
		}
		if (allWoOptions.some((o) => o.value === value)) {
			setMetaField('wo_number', value);
			setNewWo('');
			message.info('WO already exists');
			return;
		}
		setAddingWo(true);
		try {
			const wo = await dispatch(
				createClientWOV2({
					wo_number: value,
					client_id: context?.filters?.client_id,
					project_id: primaryProjectId,
				})
			);
			const label = wo?.valid_till ? `${value} (valid till ${wo.valid_till})` : value;
			setLocalWoOptions((prev) => [...prev, { value, label }]);
			setMetaField('wo_number', value);
			setNewWo('');
			message.success('WO added');
		} catch (err) {
			message.error('Failed to add WO');
		} finally {
			setAddingWo(false);
		}
	};

	// A new charge or discount inherits the slab the invoice is billed at. Defaulting
	// to Exempt let a charge move the subtotal while escaping tax entirely, and left a
	// discount with no slab to come off. Still overridable per row.
	const defaultChargeSac = useMemo(() => {
		const rated = rows.find((r) => !r.is_additional && r.sac_code && Number(r.tax_rate) > 0);
		return rated ? { sac_code: rated.sac_code, tax_rate: Number(rated.tax_rate) } : {};
	}, [rows]);

	const handleAddAdditionalCharge = (type) => {
		const isDeduction = type === 'Deduction Amount';
		setRows((prev) => [
			...prev,
			makeRow({
				item: type || ADDITIONAL_CHARGE_LABEL,
				description: type || ADDITIONAL_CHARGE_LABEL,
				is_additional: true,
				is_deduction: isDeduction,
				// A deduction carries no tax of its own — it comes off the taxable value
				// and the relief shows on the tax block. Fixed, not user-selectable.
				...(isDeduction ? { sac_code: EXEMPT_SAC_CODE, tax_rate: 0 } : defaultChargeSac),
			}),
		]);
	};

	const handleAssignSac = ({ sac_code, tax_rate }) => {
		// `??` so an explicit 0 from the modal is kept rather than re-resolved.
		const rate = tax_rate ?? sacTaxRate(sac_code, sacOptions);
		setRows((prev) =>
			prev.map((r) =>
				// A deduction's SAC is fixed to exempt; bulk-assigning a rated SAC to it
				// would tax the discount itself.
				selectedKeys.includes(r.key) && !r.is_deduction
					? { ...r, sac_code, tax_rate: rate }
					: r
			)
		);
		setSacModalOpen(false);
		setSelectedKeys([]);
	};

	const openSacModal = () => {
		if (!selectedKeys.length) {
			message.warning('Select one or more line items first.');
			return;
		}
		setSacModalOpen(true);
	};

	const handleGenerate = () => {
		if (!billTo.trim()) {
			message.warning('Bill To address is required.');
			return;
		}
		if (!shipTo.trim()) {
			message.warning('Ship To address is required.');
			return;
		}
		if (!meta.bank) {
			message.warning('Bank Name is required.');
			return;
		}
		if (!meta.account_number || !meta.account_number.trim()) {
			message.warning('Account Number is required.');
			return;
		}
		if (!meta.ifsc || !meta.ifsc.trim()) {
			message.warning('IFSC Code is required.');
			return;
		}
		if (!meta.account_type) {
			message.warning('Account Type is required.');
			return;
		}
		if (accountError) {
			message.warning(accountError);
			return;
		}
		if (ifscError) {
			message.warning(ifscError);
			return;
		}
		const deductionIssue = deductionError(computedRows);
		if (deductionIssue) {
			message.warning(deductionIssue);
			return;
		}
		onGenerate({
			meta,
			billTo,
			shipTo,
			clientGstin,
			taxMode: isNonGstClient ? taxModeChoice : undefined,
			resolvedTaxMode: taxMode,
			lineItems: computedRows,
			totals,
		});
	};

	// A deduction's SAC is fixed to exempt and shown as plain text: the discount is
	// not itself a supply, and the tax it relieves lands on the tax block instead.
	const renderSacCell = (t, record) => {
		if (record.is_deduction) return <span>Exempt</span>;
		if (!record.is_additional) return t || '-';
		return (
			<Select
				size="small"
				showSearch
				optionFilterProp="children"
				value={record.sac_code || EXEMPT_SAC}
				style={{ width: '100%' }}
				onChange={(v) => {
					if (v === EXEMPT_SAC) {
						updateRow(record.key, { sac_code: undefined, tax_rate: 0 });
						return;
					}
					updateRow(record.key, { sac_code: v, tax_rate: sacTaxRate(v, sacOptions) });
				}}
			>
				<Option value={EXEMPT_SAC}>Exempt (no tax)</Option>
				{sacOptions.map((o) => (
					<Option key={o.value} value={o.value}>
						{o.label}
					</Option>
				))}
			</Select>
		);
	};

	const lineItemColumns = [
		{ title: 'Item', dataIndex: 'item', width: 180, render: (t) => t || '-' },
		{
			title: 'Description',
			dataIndex: 'description',
			width: 220,
			render: (t, record) => (
				<Input
					size="small"
					value={record.description}
					placeholder="Description"
					onChange={(e) => updateRow(record.key, { description: e.target.value })}
					disabled={!record.is_additional}
				/>
			),
		},
		{
			title: 'SAC Code',
			dataIndex: 'sac_code',
			width: 170,
			render: renderSacCell,
		},
		{
			title: 'QTY',
			dataIndex: 'qty',
			width: 80,
			render: (t, record) =>
				record.is_additional ? (
					<span>{record.qty}</span>
				) : (
					<InputNumber
						size="small"
						min={0}
						value={record.qty}
						disabled
						style={{ width: '100%' }}
					/>
				),
		},
		{
			title: (
				<span>
					Rate / <span style={{ color: '#94a3b8' }}>Amount</span>
				</span>
			),
			dataIndex: 'rate',
			width: 110,
			// Grouped line items sum the fares across the grouped trips, so the resulting
			// amount is fixed — lock the Rate too. Additional charges remain editable.
			render: (t, record) => (
				<InputNumber
					size="small"
					min={0}
					value={record.rate}
					onChange={(v) => updateRow(record.key, { rate: v })}
					disabled={!record.is_additional}
					style={{ width: '100%' }}
					placeholder={record.is_additional ? 'Amount' : 'Rate'}
				/>
			),
		},
		{ title: 'Amount', dataIndex: 'amount', width: 100, render: formatINR },
		{ title: 'Tax Amount', dataIndex: 'taxAmount', width: 100, render: formatINR },
		{ title: 'Gross Amount', dataIndex: 'grossAmount', width: 110, render: formatINR },
		{
			title: '',
			key: 'action',
			width: 48,
			render: (_t, record) =>
				record.is_additional ? (
					<DeleteOutlined
						style={{ color: '#ef4444', cursor: 'pointer' }}
						title="Remove charge"
						onClick={() => removeRow(record.key)}
					/>
				) : null,
		},
	];

	const taxRows = buildTaxSummary(computedRows, taxMode);
	const taxColumns = [
		{ title: 'Tax Type', dataIndex: 'tax_type' },
		{ title: 'Tax Code', dataIndex: 'tax_code' },
		{ title: 'Tax Basis', dataIndex: 'basis', render: formatINR },
		{ title: 'Tax Rate', dataIndex: 'rate', render: formatRate },
		{ title: 'Tax Amount', dataIndex: 'amount', render: formatINR },
	];

	return (
		<Box>
			<Sheet>
				<Flex justifyContent="space-between" alignItems="flex-start">
					<Logo src="/ripplrLogo.png" alt="RIPPLR" />
					<Box style={{ textAlign: 'right' }}>
						<InvoiceTitle>TAX INVOICE</InvoiceTitle>
						<Muted style={{ marginTop: 8 }}>
							<div>
								<b>Invoice Number:</b> Auto (on generate)
							</div>
							<div>
								<b>Invoice Date:</b> {moment().format('DD-MM-YYYY')}
							</div>
						</Muted>
					</Box>
				</Flex>
				<SellerName>{selectedIssuer?.legalName || '—'}</SellerName>
				<SectionLabel>Bill From (Address)</SectionLabel>
				<Select
					style={{ width: 320 }}
					placeholder="Select issuer"
					value={meta.bill_from}
					onChange={(v) => {
						setMeta((prev) => ({ ...prev, bill_from: v }));
					}}
				>
					{issuers.map((o) => (
						<Option key={o.value} value={o.value}>
							{o.label}
						</Option>
					))}
				</Select>
				{selectedIssuer && (
					<Muted style={{ marginTop: 8 }}>
						<div>{selectedIssuer.address}</div>
						<div style={{ marginTop: 4 }}>
							GSTIN: {selectedIssuer.gstin || '—'} &nbsp;&nbsp; PAN:{' '}
							{selectedIssuer.pan || '—'}
						</div>
						{selectedIssuer.website ? <div>{selectedIssuer.website}</div> : null}
					</Muted>
				)}

				<Divider />

				<Flex gap="40px" style={{ gap: 40 }}>
					<Box flex="1">
						<SectionLabel>
							{isNonGstClient ? 'B2C' : 'Bill To'}
							{context?.client ? ` - ${context.client}` : ''}{' '}
							<span style={{ color: '#ef4444' }}>*</span>
						</SectionLabel>
						<TextArea
							rows={3}
							value={billTo}
							placeholder="Bill to address"
							onChange={(e) => setBillTo(e.target.value)}
						/>
						<Input
							style={{ marginTop: 8 }}
							value={clientGstin}
							placeholder="Client GSTIN"
							onChange={(e) => setClientGstin(e.target.value.toUpperCase())}
						/>
						{isNonGstClient ? (
							<>
								<SectionLabel style={{ marginTop: 8 }}>
									Tax Treatment (client not GST-registered)
								</SectionLabel>
								<Select
									style={{ width: '100%' }}
									value={taxModeChoice}
									onChange={setTaxModeChoice}
								>
									<Option value="IGST">B2C - IGST (18%)</Option>
									<Option value="CGST_SGST">B2C - CGST + SGST (9% + 9%)</Option>
									<Option value="NIL">B2C - Nil (no tax)</Option>
								</Select>
								<Muted style={{ marginTop: 4 }}>
									{taxModeChoice === 'NIL'
										? 'No tax — every line is billed as exempt.'
										: 'Taxed at each line’s SAC rate; exempt SACs stay at 0.'}
								</Muted>
							</>
						) : (
							<Muted style={{ marginTop: 4 }}>
								{sameState ? 'Same state → CGST + SGST' : 'Different state → IGST'}
							</Muted>
						)}
					</Box>
					<Box flex="1">
						<SectionLabel>
							Ship To <span style={{ color: '#ef4444' }}>*</span>
						</SectionLabel>
						<TextArea
							rows={3}
							value={shipTo}
							placeholder="Ship to address"
							onChange={(e) => setShipTo(e.target.value)}
						/>
					</Box>
				</Flex>
				<Box mt={3}>
					<Muted>
						{partyIdRows(clientGstin, clientPan).map(({ label, value }) => (
							<span key={label} style={{ marginRight: 16 }}>
								<b>{label}</b> {value}
							</span>
						))}
					</Muted>
				</Box>

				{/* Meta row */}
				<Box
					mt={3}
					style={{ background: '#f8fafc', borderRadius: 6, padding: '16px 20px' }}
				>
					<Flex gap="24px" flexWrap="wrap" style={{ gap: 24 }}>
						<Box flex="1">
							<SectionLabel>Due Date</SectionLabel>
							<DatePicker
								style={{ width: '100%' }}
								format="DD-MM-YYYY"
								value={meta.due_date}
								disabled
							/>
						</Box>
						<Box flex="1">
							<SectionLabel>PO No.</SectionLabel>
							<Select
								style={{ width: '100%' }}
								value={meta.po_number}
								onChange={(v) => setMetaField('po_number', v)}
								// eslint-disable-next-line react/no-unstable-nested-components
								dropdownRender={(menu) => (
									<>
										{menu}
										<div
											style={{
												display: 'flex',
												gap: 8,
												padding: 8,
												borderTop: '1px solid #f0f0f0',
											}}
										>
											<Input
												placeholder="New PO number"
												value={newPo}
												onChange={(e) => setNewPo(e.target.value)}
												onPressEnter={handleAddPo}
												onKeyDown={(e) => e.stopPropagation()}
											/>
											<Button
												type="text"
												icon={<PlusOutlined />}
												loading={addingPo}
												onMouseDown={(e) => e.preventDefault()}
												onClick={handleAddPo}
											>
												Add
											</Button>
										</div>
									</>
								)}
							>
								{allPoOptions.map((o) => (
									<Option key={o.value} value={o.value}>
										{o.label}
									</Option>
								))}
							</Select>
						</Box>
						<Box flex="1">
							<SectionLabel>Shipping Method</SectionLabel>
							<Box>
								{(context?.shippingMethods || []).length ? (
									context.shippingMethods.map((m) => <Tag key={m}>{m}</Tag>)
								) : (
									<Muted>-</Muted>
								)}
							</Box>
						</Box>
						<Box flex="1">
							<SectionLabel>WO No.</SectionLabel>
							<Select
								style={{ width: '100%' }}
								placeholder="Select WO"
								value={meta.wo_number}
								allowClear
								onChange={(v) => setMetaField('wo_number', v)}
								// eslint-disable-next-line react/no-unstable-nested-components
								dropdownRender={(menu) => (
									<>
										{menu}
										<div
											style={{
												display: 'flex',
												gap: 8,
												padding: 8,
												borderTop: '1px solid #f0f0f0',
											}}
										>
											<Input
												placeholder="New WO number"
												value={newWo}
												onChange={(e) => setNewWo(e.target.value)}
												onPressEnter={handleAddWo}
												onKeyDown={(e) => e.stopPropagation()}
											/>
											<Button
												type="text"
												icon={<PlusOutlined />}
												loading={addingWo}
												onMouseDown={(e) => e.preventDefault()}
												onClick={handleAddWo}
											>
												Add
											</Button>
										</div>
									</>
								)}
							>
								{allWoOptions.map((o) => (
									<Option key={o.value} value={o.value}>
										{o.label}
									</Option>
								))}
							</Select>
						</Box>
					</Flex>
				</Box>

				{/* Line item actions */}
				<Flex
					justifyContent="flex-end"
					alignItems="center"
					mt={3}
					flexWrap="wrap"
					gap="8px"
				>
					<Flex gap="10px" style={{ gap: 12 }}>
						<ActionLink icon={<PlusOutlined />} onClick={openSacModal}>
							Assign SAC Code to Selected Rows
						</ActionLink>
						<ChargeSelect
							value={null}
							placeholder="+ Add Additional Charges"
							style={{ width: 230 }}
							onSelect={(v) => handleAddAdditionalCharge(v)}
						>
							{ADDITIONAL_CHARGE_TYPES.map((t) => (
								<Option key={t} value={t}>
									{t}
								</Option>
							))}
						</ChargeSelect>
					</Flex>
				</Flex>

				{/* Line items */}
				<Box mt={2}>
					<Table
						rowKey="key"
						size="small"
						columns={lineItemColumns}
						dataSource={computedRows}
						pagination={false}
						scroll={{ x: 'max-content' }}
						rowSelection={{ selectedRowKeys: selectedKeys, onChange: setSelectedKeys }}
					/>
				</Box>

				<TotalsRow style={{ justifyContent: 'flex-end', marginTop: 8 }}>
					<Text style={{ fontWeight: 600, marginRight: 16 }}>Total Amount</Text>
					<Text style={{ fontWeight: 700 }}>{formatINR(totals.subtotal)}</Text>
				</TotalsRow>

				{/* Tax table */}
				<Box mt={3} style={{ maxWidth: 520 }}>
					<Table
						rowKey="key"
						size="small"
						columns={taxColumns}
						dataSource={taxRows}
						pagination={false}
						// eslint-disable-next-line react/no-unstable-nested-components
						summary={() => (
							<Table.Summary.Row>
								<Table.Summary.Cell index={0} colSpan={4}>
									<b>Total</b>
								</Table.Summary.Cell>
								<Table.Summary.Cell index={1}>
									<b>{formatINR(totals.taxTotal)}</b>
								</Table.Summary.Cell>
							</Table.Summary.Row>
						)}
					/>
				</Box>

				{warehouseCtx && (
					<Box mt={3} style={{ maxWidth: 520 }}>
						<InvoiceSlabTable warehouseContext={warehouseCtx} />
					</Box>
				)}

				{/* Totals */}
				<TotalsBox>
					<TotalsRow>
						<span>Subtotal</span>
						<span>{formatINR(totals.subtotal)}</span>
					</TotalsRow>
					<TotalsRow>
						<span>Tax Total</span>
						<span>{formatINR(totals.taxTotal)}</span>
					</TotalsRow>
					<GrandTotalRow>
						<span>Total</span>
						<span>{formatINR(totals.total)}</span>
					</GrandTotalRow>
				</TotalsBox>

				<Divider />

				{/* Bank details */}
				<Flex gap="32px" flexWrap="wrap" style={{ gap: 32 }}>
					<Box flex="1" style={{ minWidth: 220 }}>
						<SectionLabel>
							Bank Name <span style={{ color: '#ef4444' }}>*</span>
						</SectionLabel>
						<Select
							style={{ width: '100%' }}
							placeholder="Select Bank"
							value={meta.bank}
							onChange={(v) => setMetaField('bank', v)}
							disabled
						>
							{uniqueBankOptions.map((o) => (
								<Option key={o.value} value={o.value}>
									{o.label}
								</Option>
							))}
						</Select>
					</Box>
					<Box flex="1" style={{ minWidth: 220 }}>
						<SectionLabel>
							Account Number <span style={{ color: '#ef4444' }}>*</span>
						</SectionLabel>
						<Input
							placeholder="Account Number"
							value={meta.account_number || ''}
							maxLength={18}
							disabled
							status={accountError ? 'error' : ''}
							onChange={(e) => {
								// digits only
								const v = e.target.value.replace(/\D/g, '');
								setMetaField('account_number', v);
							}}
						/>
						{accountError ? <ErrorText>{accountError}</ErrorText> : null}
					</Box>
				</Flex>
				<Flex gap="32px" flexWrap="wrap" mt={3} style={{ gap: 32 }}>
					<Box flex="1" style={{ minWidth: 220 }}>
						<SectionLabel>
							IFSC Code <span style={{ color: '#ef4444' }}>*</span>
						</SectionLabel>
						<Input
							placeholder="IFSC Code"
							value={meta.ifsc}
							maxLength={11}
							disabled
							status={ifscError ? 'error' : ''}
							onChange={(e) => {
								const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
								setMetaField('ifsc', v);
							}}
						/>
						{ifscError ? <ErrorText>{ifscError}</ErrorText> : null}
					</Box>
					<Box flex="1" style={{ minWidth: 220 }}>
						<SectionLabel>
							Account Type <span style={{ color: '#ef4444' }}>*</span>
						</SectionLabel>
						<Select
							style={{ width: '100%' }}
							placeholder="Select Type"
							value={meta.account_type}
							onChange={(v) => setMetaField('account_type', v)}
							disabled
						>
							{accountTypes.map((o) => (
								<Option key={o.value} value={o.value}>
									{o.label}
								</Option>
							))}
						</Select>
					</Box>
				</Flex>
				<Box mt={3}>
					<SectionLabel>Remarks</SectionLabel>
					<TextArea
						rows={2}
						placeholder="Add Remarks"
						value={meta.remarks}
						onChange={(e) => setMetaField('remarks', e.target.value)}
					/>
				</Box>

				<Divider />

				{/* Terms & Conditions — exact wording from the issuer config. */}
				{terms.length ? (
					<>
						<div
							style={{
								fontSize: 15,
								fontWeight: 700,
								color: '#1f2933',
								marginBottom: 8,
							}}
						>
							Terms &amp; Conditions
						</div>
						{terms.map((t) => (
							<Muted key={t} style={{ marginBottom: 4, paddingLeft: 8 }}>
								{t}
							</Muted>
						))}
					</>
				) : null}

				<Divider />
				<Flex justifyContent="flex-end">
					<Muted>
						If you have any questions about this invoice, please contact{' '}
						{selectedIssuer?.contactEmail || 'accounts@ripplr.in'}
					</Muted>
				</Flex>
			</Sheet>

			{/* Footer actions */}
			<Flex justifyContent="space-between" mt={3}>
				<Button style={{ height: 40, padding: '0 24px', borderRadius: 6 }} onClick={onBack}>
					← Back
				</Button>
				<PrimaryButton onClick={handleGenerate}>{generateLabel} →</PrimaryButton>
			</Flex>

			<AssignSacCodeModal
				visible={sacModalOpen}
				options={sacOptions}
				initialValue={sacOptions[0]?.value}
				onSave={handleAssignSac}
				onClose={() => setSacModalOpen(false)}
			/>
		</Box>
	);
}

InvoiceGenerationStep.propTypes = {
	context: PropTypes.object,
	onBack: PropTypes.func.isRequired,
	onGenerate: PropTypes.func.isRequired,
	generateLabel: PropTypes.string,
};

InvoiceGenerationStep.defaultProps = {
	generateLabel: 'Generate',
	context: {},
};

export default InvoiceGenerationStep;
